import { TableCell, TableRow } from "@/components/ui/table"
import { SleepRecord } from "@/types/sleep"
import { motion } from "framer-motion"
import { cn } from "@/lib/utils"
import { SleepDateCell } from "./SleepDateCell"
import { SleepWindowCell } from "./SleepWindowCell"
import { SleepDurationCell } from "./SleepDurationCell"
import { SleepQualityCell } from "./SleepQualityCell"
import { useMediaQuery } from "@/hooks/use-media-query"
import { FileText, Info } from "lucide-react"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"

interface SleepTableRowProps {
  record: SleepRecord
  timezone?: string
  showNotes?: boolean
}

export function SleepTableRow({ record, timezone, showNotes = false }: SleepTableRowProps) {
  const isMobile = useMediaQuery("(max-width: 479px)")
  
  const calculateDurationMinutes = (startTime: string, endTime: string): number => {
    try {
      const start = new Date(startTime)
      const end = new Date(endTime)
      
      // Check if dates are valid
      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        console.error("Invalid date format:", { startTime, endTime })
        return 0
      }
      
      // Calculate duration in minutes
      const durationMs = end.getTime() - start.getTime()
      return Math.round(durationMs / (1000 * 60))
    } catch (error) {
      console.error("Error calculating duration:", error)
      return 0
    }
  }

  // Use duration_minutes if available, otherwise calculate it (only if end_time exists)
  const duration = record.duration_minutes || 
                  (record.end_time ? calculateDurationMinutes(record.start_time, record.end_time) : null)
  
  const hoursOfSleep = duration ? duration / 60 : null

  // Use mobile-specific classes
  const cellClasses = cn(
    "mobile-table-cell",
    isMobile ? "py-1 px-1" : "px-1 py-1 xs:px-1.5 xs:py-1.5 sm:px-3 sm:py-3"
  )

  // Check if notes exist and are not empty
  const hasNotes = record.notes && record.notes.trim().length > 0;

  return (
    <motion.tr
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className={cn(
        "border-b border-purple-500/10",
        "transition-colors duration-200",
        "hover:bg-purple-500/5"
      )}
    >
      <TableCell className={cellClasses}>
        <SleepDateCell startTime={record.start_time} timezone={timezone} />
      </TableCell>
      <TableCell className={cellClasses}>
        <SleepDateCell startTime={record.end_time} timezone={timezone} />
      </TableCell>
      <TableCell className={cellClasses}>
        <SleepWindowCell startTime={record.start_time} endTime={record.end_time} timezone={timezone} />
      </TableCell>
      <TableCell className={cellClasses}>
        <SleepDurationCell durationMinutes={duration} />
      </TableCell>
      <TableCell className={cellClasses}>
        <SleepQualityCell hoursOfSleep={hoursOfSleep} />
      </TableCell>
      {showNotes && (
        <TableCell className={cellClasses}>
          {hasNotes ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center cursor-help">
                    <FileText className="h-3.5 w-3.5 text-purple-400 mr-1" />
                    <span className="text-2xs xs:text-xs text-gray-300 truncate max-w-[100px]">
                      {record.notes.substring(0, 15)}{record.notes.length > 15 ? '...' : ''}
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[300px] text-xs">
                  <p>{record.notes}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <span className="text-2xs xs:text-xs text-gray-500">No notes</span>
          )}
        </TableCell>
      )}
    </motion.tr>
  )
}
